import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import AppSwitch from "components/Shared/AppSwitch";
import { useAuth } from "hooks";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { useLazyGetDDLShipmentListForCOCQuery } from "store/services/shipmentApi";
import { useListGlobalListQuery } from "store/services/globalListApi";
import { GLOBAL_LIST_CONSTANTS } from "data/static/globalListOptions";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import { useListListQuery } from "store/services/listService";
import NCHLQR from "utils/generateNCHLQRCode";
import { getSocketInstance } from "socket/socket.io";

const INITIAL_VALUE = {
  account: null,
  entity: null,
  branch: null,
  shipmentId: null, // holds an object when Collection Method is NCHL, an array otherwise
  priceIds: [],
  collectionMethod: "",
  bankName: null,
  actualAmount: "",
  collectionAmount: "",
  remittanceRemarks: "",
  isActive: true,
  source: "COC Collection",
};

const CocCollectionForm = ({
  cocCollectionDetails = null,
  isBusy = false,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { isAccountAdmin, isSystemAdmin, viewEntity, loggedInUser } = useAuth();
  const nchlQRRef = useRef(null);
  const paymentSuccessHandled = useRef(false);
  const [isPaymentDone, setIsPaymentDone] = useState(false);
  const [paymentResponseData, setPaymentResponseData] = useState(null);
  const [currentTraceId, setCurrentTraceId] = useState(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    trigger,
  } = useForm({
    defaultValues: {
      ...INITIAL_VALUE,
      account: viewEntity?.entity?.account || null,
      entity: viewEntity?.entity || null,
    },
    mode: "onChange",
  });

  const { data: bankNameOptions, isLoading: loadingBankNameOptions } =
    useListGlobalListQuery({
      type: GLOBAL_LIST_CONSTANTS.BANK_NAME,
      isSubType: false,
      includeChildren: true,
      status: "Active",
    });

  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [fetchBranches, { data: branchList, isLoading: loadingBranch }] =
    useLazyGetDDLBranchListQuery();
  const [
    fetchShipments,
    {
      data: shipmentData,
      isLoading: loadingShipments,
      isFetching: isFetchingShipments,
      error: shipmentError,
    },
  ] = useLazyGetDDLShipmentListForCOCQuery();
  const { data: entityTypeList = [] } = useListListQuery(
    {
      type: ENTITY_LIST_CONSTANTS.PAYMENT_METHOD,
      status: "Active",
    },
    { skip: false, refetchOnMountOrArgChange: true },
  );

  // Memoize mapped lists
  const _mappedAccountTypeList = useMemo(
    () =>
      Array.isArray(entityTypeList)
        ? entityTypeList.map((item) => ({
          label: item?.name || "",
          value: item?._id || "",
        }))
        : [],
    [entityTypeList],
  );

  const [pricingData, setPricingData] = useState([]);
  const [selectedPriceIds, setSelectedPriceIds] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [invalidShipments, setInvalidShipments] = useState([]);

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchActualAmount = useWatch({
    control,
    name: "actualAmount",
  });
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchBranch = useWatch({ control, name: "branch" });
  const _watchShipmentId = useWatch({ control, name: "shipmentId" });
  const _watchPriceIds = useWatch({ control, name: "priceIds" });
  const _watchCollectionMethod = useWatch({
    control,
    name: "collectionMethod",
  });
  const _watchCollectionAmount = useWatch({
    control,
    name: "collectionAmount",
  });

  const selectedCollectionMethod = useMemo(() => {
    if (!Array.isArray(entityTypeList) || !_watchCollectionMethod) return null;
    return entityTypeList.find((item) => item._id === _watchCollectionMethod);
  }, [_watchCollectionMethod, entityTypeList]);

  const isNCHL = selectedCollectionMethod?.name === "NCHL";

  const selectedShipments = useWatch({
    control,
    name: "shipmentId",
  });

  // Get unpaid price IDs for NCHL payment
  const unpaidPriceIds = useMemo(() => {
    if (!_watchPriceIds?.length) return [];
    return _watchPriceIds
      .filter((price) => price.paymentStatus !== "Paid")
      .map((price) => price._id)
      .filter(Boolean);
  }, [_watchPriceIds]);

  // Handle NCHL Payment Success
  const handleNCHLPaymentSuccess = useCallback(
    (response) => {
      // Reentrancy guard. `handleNCHLPaymentSuccess` can fire from two
      // independent sources for the same payment: (1) NCHLQR's onSuccess
      // prop when debitStatus === "000", and (2) the parent's socket
      // listener on `payment-success/coc-collection/${currentTraceId}`.
      // Both can arrive close together — without this guard we toast and
      // navigate twice. Reset in the priceIds-watcher effect below so a
      // new payment attempt can notify again.
      if (paymentSuccessHandled.current) return;
      paymentSuccessHandled.current = true;

      let paymentData = null;
      let cocCollectionId = null;
      let redirectUrl = null;

      // Extract data from response
      if (response?.cocCollectionId) {
        cocCollectionId = response.cocCollectionId;
        redirectUrl = response.redirectUrl;
        paymentData = response.paymentData;
      } else if (response?.paymentData) {
        paymentData = response.paymentData;
        cocCollectionId = response.paymentData.cocCollectionRef;
      } else if (response?.sessionSrlNo || response?.debitStatus) {
        // Raw NCHL status only — the debit is confirmed but the backend
        // hasn't finished creating the collection record yet (that only
        // happens via the websocket messageHandler). Don't treat this as
        // terminal: there's no redirect data yet, and locking the guard
        // here would silently swallow the real socket event when it
        // arrives. Reset the guard and bail so the follow-up call from
        // the socket can still do the work.
        paymentSuccessHandled.current = false;
        toast.info("Payment confirmed, finalizing collection...");
        return;
      }

      if (paymentData) {
        setPaymentResponseData(paymentData);
        toast.success("NCHL Payment successful! COC Collection created.");
        nchlQRRef.current?.handleCloseQR();

        const paidPriceIds = Array.isArray(paymentData.priceIds)
          ? paymentData.priceIds
          : [];
        // Update pricing data to show "Paid" status
        setPricingData((prevData) =>
          prevData.map((item) => {
            const priceIdStr = item.price._id.toString();
            const isPaid = paidPriceIds.some(
              (id) => id.toString() === priceIdStr,
            );

            if (isPaid) {
              return {
                ...item,
                price: {
                  ...item.price,
                  paymentStatus: "Paid",
                },
              };
            }
            return item;
          }),
        );

        // Update selected priceIds. RHF's setValue() does not accept a
        // functional updater (unlike React's useState setter) — passing a
        // function here would set the field to the function itself, not
        // to an array. Read the current value from the form state and
        // map synchronously. _watchPriceIds is available in this
        // closure but may be stale relative to the latest setValue calls;
        // control._formValues reflects the most recently committed
        // value, which is what we want here.
        const currentPriceIds = control._formValues.priceIds || [];
        setValue(
          "priceIds",
          currentPriceIds.map((price) => {
            const priceIdStr = price._id.toString();
            const isPaid = paidPriceIds.some(
              (id) => id.toString() === priceIdStr,
            );

            if (isPaid) {
              return { ...price, paymentStatus: "Paid" };
            }
            return price;
          }),
        );

        setIsPaymentDone(true);

        // Redirect to COC Collection detail page after successful payment
        if (cocCollectionId && redirectUrl) {
          toast.success("Redirecting to COC Collection details...", {
            autoClose: 1500,
          });
          setTimeout(() => {
            navigate(redirectUrl);
          }, 1500);
        } else if (cocCollectionId) {
          // Fallback if redirectUrl is not provided
          const fallbackUrl = `/app/crm/coc-collection/${cocCollectionId}/detail`;
          toast.success("Redirecting to COC Collection details...", {
            autoClose: 1500,
          });
          setTimeout(() => {
            navigate(fallbackUrl);
          }, 1500);
        } else {
          console.error("No COC Collection ID found for redirect");
        }
      } else {
        // No usable payment data — let a future attempt re-fire the
        // success handler instead of permanently swallowing it.
        paymentSuccessHandled.current = false;
        console.log(" No payment data found in response");
      }
    },
    [setValue, navigate, control],
  );

  // Reset payment status when price IDs change
  useEffect(() => {
    setIsPaymentDone(false);
    setPaymentResponseData(null);
    // Allow a fresh NCHL payment attempt to re-fire onSuccess now that
    // the selected priceIds have changed (a new payment flow).
    paymentSuccessHandled.current = false;
  }, [_watchPriceIds]);

  useEffect(() => {
    if (!currentTraceId) return;

    const socket = getSocketInstance();

    if (!socket) return;

    const uniqueEvent = `payment-success/coc-collection/${currentTraceId}`;

    const handleSuccess = (response) => {
      if (response.validationTraceId !== currentTraceId) {
        return;
      }

      handleNCHLPaymentSuccess(response);
    };

    socket.on(uniqueEvent, handleSuccess);

    return () => {
      // ONLY remove the listener here. Do NOT call setCurrentTraceId(null)
      // — React.StrictMode deliberately unmounts/remounts effects twice in
      // dev, and resetting currentTraceId in cleanup causes the second
      // mount's effect to see null and exit early, leaving the listener
      // permanently unregistered. The next setCurrentTraceId(newTraceId)
      // call from the click handler will trigger a re-run that swaps in
      // the new listener correctly.
      socket.off(uniqueEvent, handleSuccess);
    };
  }, [currentTraceId, handleNCHLPaymentSuccess]);

  useEffect(() => {
    if (shipmentError) {
      toast.error("Failed to load shipments. Please try again.");
    }
  }, [shipmentData, shipmentError, loadingShipments]);

  useEffect(() => {
    if (shipmentData?.data && !cocCollectionDetails) {
      setValue("shipmentId", null);
      setValue("priceIds", []);
      setSelectedPriceIds([]);
    }
  }, [shipmentData, setValue, cocCollectionDetails]);

  useEffect(() => {
    if (!cocCollectionDetails) return;

    const accountObj =
      accounts?.find(
        (acc) =>
          acc._id ===
          (cocCollectionDetails.account?._id || cocCollectionDetails.account),
      ) || null;
    const entityObj =
      entities?.find(
        (ent) =>
          ent._id ===
          (cocCollectionDetails.entity?._id || cocCollectionDetails.entity),
      ) || null;
    const branchObj =
      branchList?.find(
        (b) =>
          b._id ===
          (cocCollectionDetails.branch?._id || cocCollectionDetails.branch),
      ) || null;

    const bankNameObj =
      bankNameOptions?.find(
        (opt) =>
          opt._id ===
          (typeof cocCollectionDetails?.bankName === "object"
            ? cocCollectionDetails.bankName?._id
            : cocCollectionDetails.bankName),
      ) || null;

    const detailMethod = entityTypeList.find(
      (item) => item._id === cocCollectionDetails.collectionMethod
    );
    const detailIsNCHL = detailMethod?.name === "NCHL";
    const shipmentArray = cocCollectionDetails.shipmentId || [];
    const mapShipment = (s) => ({
      _id: s._id || s,
      shipmentId: s.shipmentId || "",
      shipmentAmount: s.shipmentAmount || 0,
      sourceBranch: s.sourceBranch || "",
      destinationBranch: s.destinationBranch || "",
    });
    const shipmentIdValue = detailIsNCHL
      ? (shipmentArray[0] ? mapShipment(shipmentArray[0]) : null)
      : shipmentArray.map(mapShipment);
    reset({
      account: accountObj,
      entity: entityObj,
      branch: branchObj,
      shipmentId: shipmentIdValue,
      priceIds: [],
      collectionMethod: cocCollectionDetails.collectionMethod || "",
      bankName: bankNameObj ? bankNameObj._id : null,
      actualAmount: cocCollectionDetails.actualAmount || "",
      collectionAmount: cocCollectionDetails.collectionAmount || "",
      remittanceRemarks: cocCollectionDetails.remittanceRemarks || "",
      isActive: cocCollectionDetails.isActive ?? true,
    });

    if (accountObj?._id && entityObj?._id && branchObj?._id) {
      fetchShipments({
        account: accountObj._id,
        entity: entityObj._id,
        branch: branchObj._id,
      });
    }
  }, [
    cocCollectionDetails,
    accounts,
    entities,
    branchList,
    bankNameOptions,
    entityTypeList,
    reset,
    fetchShipments,
  ]);

  useEffect(() => {
    const hasShipmentSelection = isNCHL
      ? !!_watchShipmentId?._id
      : Array.isArray(_watchShipmentId) && _watchShipmentId.length > 0;
    if (
      Array.isArray(shipmentData?.data) &&
      Array.isArray(shipmentData?.priceItems) &&
      hasShipmentSelection
    ) {
      const selectedShipmentIds = isNCHL
        ? [_watchShipmentId._id]
        : _watchShipmentId.map((s) => s._id);
      const filteredPriceItems = shipmentData.priceItems
        .filter(
          (price) =>
            price?.shipment?._id &&
            selectedShipmentIds.includes(price.shipment._id),
        )
        .map((price) => ({
          shipmentId: price.shipmentId || "",
          price: {
            _id: price._id,
            feeItem: price.feeItem || "Unknown",
            charge: price.charge || 0,
            chargeType: price.chargeType || "Charge",
            discountStatus: price.discountStatus || null,
            payer: price.payer || "Unknown",
            paymentLocation: price.paymentLocation || "Unknown",
            paymentStatus: price.paymentStatus || "Not Paid",
            user: price.shipment.customerId?.customerUser || {
              name: "Unknown",
            },
            createdAt: price.createdAt || new Date(),
            shipmentId: price.shipmentId || "",
            sourceBranch: price.shipment.sourceBranch || "",
            destinationBranch: price.shipment.destinationBranch || "",
          },
        }));

      setPricingData(filteredPriceItems);
      if (cocCollectionDetails) {
        const cocPriceIds = (cocCollectionDetails.priceIds || []).map(
          (p) => p._id || p,
        );
        const matchingPriceIds = filteredPriceItems
          .filter((item) => cocPriceIds.includes(item.price._id))
          .map((item) => item.price);
        setValue("priceIds", matchingPriceIds);
        setSelectedPriceIds(matchingPriceIds.map((p) => p._id));
      } else if (filteredPriceItems.length > 0) {
        const allIds = filteredPriceItems.map((item) => item.price._id);
        setSelectedPriceIds(allIds);
        setValue(
          "priceIds",
          filteredPriceItems.map((item) => item.price),
        );
      }
    } else if (!cocCollectionDetails) {
      setPricingData([]);
      setSelectedPriceIds([]);
      setValue("priceIds", []);
    }
  }, [shipmentData, _watchShipmentId, setValue, cocCollectionDetails, isNCHL]);

  useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
      });
    }
    if (_watchAccount?._id && _watchEntity?._id) {
      fetchBranches({ account: _watchAccount._id, entity: _watchEntity._id });
    }
  }, [
    _watchAccount?._id,
    _watchEntity?._id,
    fetchEntities,
    fetchBranches,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity?.account?._id,
  ]);

  useEffect(() => {
    if (
      _watchAccount?._id &&
      _watchEntity?._id &&
      _watchBranch?._id &&
      !cocCollectionDetails
    ) {
      fetchShipments({
        account: _watchAccount._id,
        entity: _watchEntity._id,
        branch: _watchBranch._id,
      });
    }
  }, [
    _watchAccount?._id,
    _watchEntity?._id,
    _watchBranch?._id,
    fetchShipments,
    cocCollectionDetails,
  ]);

  // Update actual and collection amounts based on selected priceIds
  useEffect(() => {
    if (_watchPriceIds?.length > 0) {
      const totalAmount = _watchPriceIds.reduce((sum, price) => {
        if (price.chargeType === "Discount") return sum - (price.charge || 0);
        return sum + (price.charge || 0);
      }, 0);
      setValue("actualAmount", totalAmount);
      setValue("collectionAmount", totalAmount);
    } else {
      setValue("actualAmount", "");
      setValue("collectionAmount", "");
    }
  }, [_watchPriceIds, setValue]);

  // Reset bankName if collectionMethod is not Bank Transfer
  useEffect(() => {
    if (selectedCollectionMethod !== "Bank") {
      setValue("bankName", null);
    }
  }, [selectedCollectionMethod, setValue]);

  // Handle price item selection
  const handlePriceSelection = (price) => {
    const isSelected = selectedPriceIds.includes(price._id);
    let updatedPriceIds;
    if (isSelected) {
      updatedPriceIds = selectedPriceIds.filter((id) => id !== price._id);
    } else {
      updatedPriceIds = [...selectedPriceIds, price._id];
    }
    setSelectedPriceIds(updatedPriceIds);
    setValue(
      "priceIds",
      pricingData
        .map((shipment) => shipment.price)
        .filter((p) => updatedPriceIds.includes(p._id)),
    );
    trigger("priceIds");
  };

  // Handle dialog confirmation
  const handleDialogConfirm = (data) => {
    setOpenDialog(false);
    const validShipmentIds = pricingData
      .filter((shipment) => selectedPriceIds.includes(shipment.price._id))
      .map((shipment) => shipment.price.shipmentId);
    const shipmentIds = isNCHL
      ? data.shipmentId?._id &&
        validShipmentIds.includes(data.shipmentId.shipmentId)
        ? [data.shipmentId._id]
        : []
      : Array.isArray(data.shipmentId)
        ? data.shipmentId
          .filter((s) => s._id && validShipmentIds.includes(s.shipmentId))
          .map((s) => s._id)
        : [];
    const actualAmount = Number(data.actualAmount);
    const collectionAmount = Number(data.collectionAmount);
    const payload = {
      ...data,
      account: data?.account?._id || null,
      entity: data?.entity?._id || null,
      branch: data?.branch?._id || null,
      shipmentId: shipmentIds,
      priceIds: data?.priceIds?.map((p) => p._id) || [],
      actualAmount,
      collectionAmount,
      remittanceRemarks: data?.remittanceRemarks || "",
      bankName:
        selectedCollectionMethod?.name === "Bank" ? data.bankName : null,
      source: "COC Collection",
      paymentData: paymentResponseData,
    };

    onSubmit(payload);
  };

  // Handle dialog cancellation
  const handleDialogCancel = () => {
    setOpenDialog(false);
    toast.error("Shipment's fee item not selected.");
  };

  // Validate and handle form submission
  const handleTransfer = (data) => {
    const actualAmount = Number(data.actualAmount);
    const collectionAmount = Number(data.collectionAmount);

    if (actualAmount !== collectionAmount) {
      toast.error(
        actualAmount > collectionAmount
          ? "Actual Amount is more than Collection Amount."
          : "Collection Amount is more than Actual Amount.",
      );
      return;
    }

    if (!_watchPriceIds?.length) {
      toast.error("Please select at least one fee item.");
      return;
    }

    const priceShipmentIds = pricingData
      .filter((shipment) => selectedPriceIds.includes(shipment.price._id))
      .map((shipment) => shipment.price.shipmentId);
    if (isNCHL) {
      const shipmentHasNoPrice =
        _watchShipmentId?.shipmentId &&
        !priceShipmentIds.includes(_watchShipmentId.shipmentId);
      if (shipmentHasNoPrice) {
        setInvalidShipments([_watchShipmentId.shipmentId]);
        setOpenDialog(true);
        return;
      }
    } else {
      const shipmentsWithoutPrice = (Array.isArray(_watchShipmentId) ? _watchShipmentId : []).filter(
        (s) => !priceShipmentIds.includes(s.shipmentId)
      );
      if (shipmentsWithoutPrice.length > 0) {
        setInvalidShipments(shipmentsWithoutPrice.map((s) => s.shipmentId));
        setOpenDialog(true);
        return;
      }
    }
    const shipmentIds = isNCHL
      ? (data?.shipmentId?._id ? [data.shipmentId._id] : [])
      : (Array.isArray(data?.shipmentId) ? data.shipmentId.filter((s) => s._id).map((s) => s._id) : []);
    const payload = {
      ...data,
      account: data?.account?._id || null,
      entity: data?.entity?._id || null,
      branch: data?.branch?._id || null,
      shipmentId: shipmentIds,
      priceIds: data?.priceIds?.map((p) => p._id) || [],
      actualAmount,
      collectionAmount,
      remittanceRemarks: data?.remittanceRemarks || "",
      bankName:
        selectedCollectionMethod?.name === "Bank" ? data.bankName : null,
      source: "COC Collection",
      paymentData: paymentResponseData,
    };

    onSubmit(payload);
  };

  return (
    <AppForm
      loading={
        isBusy ||
        loadingBankNameOptions ||
        loadingAccounts ||
        loadingEntities ||
        loadingBranch
      }
    >
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Account is required" }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? null}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", null);
                      setValue("branch", null);
                      setValue("shipmentId", null);
                      setValue("priceIds", []);
                      setSelectedPriceIds([]);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Accounts..."
                    disabled={!!cocCollectionDetails || isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Entity is required" }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    required
                    label="Entity"
                    value={field.value ?? null}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("branch", null);
                      setValue("shipmentId", null);
                      setValue("priceIds", []);
                      setSelectedPriceIds([]);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    disabled={!!cocCollectionDetails}
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item md={6}>
            <Controller
              name="collectionMethod"
              control={control}
              rules={{ required: "Collection Method is required" }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  fullWidth
                  required
                  label="Collection Method"
                  options={_mappedAccountTypeList}
                  getOptionLabel={(opt) => opt.label || ""}
                  getOptionValue={(opt) => opt.value || ""}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    const newMethod = entityTypeList.find(
                      (item) => item._id === e.target.value,
                    );
                    const nowNCHL = newMethod?.name === "NCHL";
                    // Convert the existing shipment selection's shape to match
                    // the new mode instead of clearing it. Branch, shipment,
                    // and priceIds all stay intact — only remarks resets.
                    let convertedShipmentId;
                    if (nowNCHL) {
                      convertedShipmentId = Array.isArray(_watchShipmentId)
                        ? _watchShipmentId[0] || null
                        : _watchShipmentId || null;
                    } else {
                      convertedShipmentId = Array.isArray(_watchShipmentId)
                        ? _watchShipmentId
                        : _watchShipmentId?._id
                          ? [_watchShipmentId]
                          : [];
                    }
                    setValue("shipmentId", convertedShipmentId);
                    setValue(
                      "remittanceRemarks",
                      nowNCHL ? convertedShipmentId?.shipmentId || "" : ""
                    );
                  }}
                  error={!!errors.collectionMethod}
                  helperText={errors.collectionMethod?.message}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="branch"
              control={control}
              rules={{ required: "Branch is required" }}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  required
                  label="Branch Name"
                  value={field.value ?? null}
                  onChange={(_, data) => {
                    field.onChange(data);
                    setValue("shipmentId", null);
                    setValue("priceIds", []);
                    setSelectedPriceIds([]);
                  }}
                  getOptionLabel={(option) => option?.name ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={branchList || []}
                  loading={loadingBranch}
                  loadingText="Loading Branches..."
                  helperText={errors?.branch?.message}
                  error={!!errors?.branch}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{
                required: isNCHL
                  ? "A shipment is required"
                  : "At least one shipment is required",
                validate: (value) =>
                  isNCHL
                    ? value?._id
                      ? true
                      : "A shipment is required"
                    : Array.isArray(value) && value.length > 0
                      ? true
                      : "At least one shipment is required",
              }}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  multiple={!isNCHL}
                  required
                  label={isNCHL ? "Shipment" : "Shipments"}
                  value={
                    isNCHL
                      ? field.value || null
                      : Array.isArray(field.value)
                        ? field.value
                        : []
                  }
                  onChange={(_, data) => {
                    if (isNCHL) {
                      field.onChange(data);
                      setValue("priceIds", []);
                      setSelectedPriceIds([]);
                      setValue("remittanceRemarks", data?.shipmentId || "");
                    } else {
                      const uniqueData = (data || []).filter(
                        (item, index, self) =>
                          index === self.findIndex((t) => t._id === item._id),
                      );
                      field.onChange(uniqueData);
                      setValue("priceIds", []);
                      setSelectedPriceIds([]);
                    }
                  }}
                  getOptionLabel={(option) => option?.shipmentId || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  disabled={isFetchingShipments}
                  options={
                    loadingShipments
                      ? []
                      : Array.isArray(shipmentData?.data)
                        ? shipmentData.data
                        : cocCollectionDetails?.shipmentId?.map((s) => ({
                          _id: s._id || s,
                          shipmentId: s.shipmentId || "",
                        })) || []
                  }
                  loading={isFetchingShipments}
                  loadingText="Data is loading..."
                  noOptionsText={
                    !_watchBranch
                      ? "Select a branch first"
                      : "No shipments available"
                  }
                  helperText={
                    isFetchingShipments
                      ? "Please wait, shipments are loading..."
                      : errors?.shipmentId?.message
                  }
                  error={!!errors?.shipmentId && !isFetchingShipments}
                />
              )}
            />
            {isFetchingShipments && (
              <LinearProgress sx={{ mt: 0.5, borderRadius: 1 }} />
            )}
          </Grid>
          {selectedCollectionMethod?.name === "Bank" && (
            <Grid item md={6}>
              <Controller
                name="bankName"
                control={control}
                rules={{ required: "Bank Name is required for Bank Transfer" }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Bank Name"
                    required
                    value={
                      bankNameOptions?.find((opt) => opt._id === field.value) ||
                      null
                    }
                    onChange={(_, data) => field.onChange(data?._id || null)}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={bankNameOptions || []}
                    loading={loadingBankNameOptions}
                    loadingText="Loading Bank Names..."
                    error={!!errors.bankName}
                    helperText={errors.bankName?.message}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item md={6}>
            <Controller
              name="actualAmount"
              control={control}
              rules={{
                required: "Actual Amount is required",
                min: { value: 0, message: "Amount must be non-negative" },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Actual Amount"
                  fullWidth
                  size="small"
                  required
                  type="number"
                  error={!!errors?.actualAmount}
                  helperText={errors?.actualAmount?.message}
                  disabled
                />
              )}
            />
          </Grid>
          <Grid item md={6} sx={{ display: "none" }}>
            <Controller
              name="collectionAmount"
              control={control}
              rules={{
                required: "Collection Amount is required",
                min: { value: 0, message: "Amount must be non-negative" },
                validate: (value) => {
                  const total = _watchPriceIds?.reduce((sum, price) => {
                    if (price.chargeType === "Discount")
                      return sum - (price.charge || 0);
                    return sum + (price.charge || 0);
                  }, 0);
                  return Number(value) === Number(total)
                    ? true
                    : "Collection Amount must match the total of selected fee items";
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Collection Amount"
                  fullWidth
                  size="small"
                  required
                  type="number"
                  error={!!errors?.collectionAmount}
                  helperText={errors?.collectionAmount?.message}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="remittanceRemarks"
              control={control}
              rules={{ required: "Please enter remittance remarks." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Remittance Remarks"
                  variant="outlined"
                  size="small"
                  required
                  error={!!errors.remittanceRemarks}
                  helperText={errors.remittanceRemarks?.message}
                  inputProps={{ maxLength: 255 }}
                />
              )}
            />
          </Grid>

          {/* NCHL Payment Section */}
          <Grid item xs={12}>
            {isNCHL && !isPaymentDone && unpaidPriceIds.length > 0 && (
              <div className="mt-2 flex items-center gap-2">
                <Button
                  variant="outlined"
                  color="success"
                  onClick={async () => {
                    const traceId = await nchlQRRef.current?.handleGenerateQR();
                    if (traceId) {
                      setCurrentTraceId(traceId);
                    }
                  }}
                  disabled={
                    isBusy ||
                    !_watchPriceIds?.length ||
                    Number(_watchCollectionAmount) <= 0 ||
                    !!cocCollectionDetails ||
                    !isNCHL
                  }
                >
                  Pay Via NCHL
                </Button>

                <NCHLQR
                  ref={nchlQRRef}
                  addedUser={loggedInUser?._id}
                  amount={Number(_watchActualAmount) || 0}
                  priceIds={unpaidPriceIds}
                  shipmentId={_watchShipmentId?.shipmentId || ""}
                  onSuccess={handleNCHLPaymentSuccess}
                />
              </div>
            )}
          </Grid>

          {((isNCHL && selectedShipments?._id) ||
            (!isNCHL &&
              Array.isArray(selectedShipments) &&
              selectedShipments.length > 0)) && (
              <Grid item xs={12}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={
                                pricingData.length > 0 &&
                                selectedPriceIds.length === pricingData.length
                              }
                              indeterminate={
                                selectedPriceIds.length > 0 &&
                                selectedPriceIds.length < pricingData.length
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  const allIds = pricingData.map(
                                    (shipment) => shipment.price._id,
                                  );
                                  const allPrices = pricingData.map(
                                    (shipment) => shipment.price,
                                  );
                                  setSelectedPriceIds(allIds);
                                  setValue("priceIds", allPrices);
                                  trigger("priceIds");
                                } else {
                                  setSelectedPriceIds([]);
                                  setValue("priceIds", []);
                                  trigger("priceIds");
                                }
                              }}
                            />
                          </div>
                        </TableCell>
                        <TableCell>Shipment ID</TableCell>
                        <TableCell>Fee Item</TableCell>
                        <TableCell>Charge</TableCell>
                        <TableCell>Payer</TableCell>
                        <TableCell>Payment Location</TableCell>
                        <TableCell>Payment Status</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Created At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pricingData.map((shipment) => (
                        <TableRow
                          key={shipment.price._id}
                          sx={{
                            border: invalidShipments.includes(shipment.shipmentId)
                              ? "2px solid red"
                              : "none",
                            backgroundColor:
                              shipment.price.paymentStatus === "Paid"
                                ? "#e8f5e9"
                                : "inherit",
                          }}
                        >
                          <TableCell>
                            <Checkbox
                              checked={selectedPriceIds.includes(
                                shipment.price._id,
                              )}
                              onChange={() =>
                                handlePriceSelection(shipment.price)
                              }
                            />
                          </TableCell>
                          <TableCell>{shipment.shipmentId}</TableCell>
                          <TableCell>{shipment.price.feeItem.name}</TableCell>
                          <TableCell>{shipment.price.charge}</TableCell>
                          <TableCell>{shipment.price.payer}</TableCell>
                          <TableCell>{shipment.price.paymentLocation}</TableCell>
                          <TableCell>
                            <span
                              style={{
                                color:
                                  shipment.price.paymentStatus === "Paid"
                                    ? "green"
                                    : "red",
                                fontWeight: "bold",
                              }}
                            >
                              {shipment.price.paymentStatus}
                            </span>
                          </TableCell>
                          <TableCell>{shipment.price.user.name}</TableCell>
                          <TableCell>
                            {new Date(shipment.price.createdAt).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          {cocCollectionDetails && (
            <Grid item md={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button disabled={isBusy} onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isBusy || !_watchPriceIds?.length || isNCHL}
          onClick={handleSubmit(handleTransfer)}
        >
          {cocCollectionDetails ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>

      <Dialog open={openDialog} onClose={handleDialogCancel}>
        <DialogTitle>Shipment Fee Item Missing</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The following shipments have no selected fee items:{" "}
            {invalidShipments.join(", ")}. Want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleDialogConfirm({ ...control._formValues })}
            autoFocus
          >
            Yes
          </Button>
          <Button onClick={handleDialogCancel}>No</Button>
        </DialogActions>
      </Dialog>
    </AppForm>
  );
};

export default CocCollectionForm;
